/**
 * Ce que la page « Programme » DIT d'un chapitre — la logique, hors du JSX
 * (chantier « manuel vivant », Φ-MV-1, MV-CNT-06).
 *
 * ─── Pourquoi un module à part ──────────────────────────────────────────────
 *
 * La page est un rendu ; les phrases, les comptes et les regroupements sont
 * ici, purs, et testés (`programme-format.test.ts`). Trois règles de la console
 * y sont tenues une fois plutôt qu'à chaque ligne de JSX :
 *
 *  1. AUCUN CODE INTERNE À L'ÉCRAN (MV-UX-04) — une notion se dit par son rang
 *     et son titre, un prérequis par son nom, un savoir par son énoncé, une
 *     attente par son énoncé. Les codes restent des clés : React s'en sert,
 *     l'œil ne les voit pas.
 *  2. AUCUN POURCENTAGE (`invariant-agregat.ts`, règle 1). La part de rythme
 *     d'une notion est une fraction du temps de classe : elle se dit en
 *     SÉANCES SUR DIX, pas en pour cent. Ce n'est pas une mesure d'élève, mais
 *     un écran sans aucun « % » n'a pas à expliquer pourquoi celui-là serait
 *     permis.
 *  3. LE PROGRAMME PARLE EN MOTS — « maîtrise attendue », « en construction »,
 *     « réinvestie », jamais la flèche ni l'étoile du document.
 */
import {
  denombrer,
  chapitreEstUnSocle,
  libelleNiveauScolaire,
  type AppuiProgrammeDto,
  type RessourcesDAppuiDto,
  type AttenteNotionDto,
  type ChapitreProgrammeResponse,
  type ContentStatus,
  type JalonAttenteDto,
  type Matiere,
  type NiveauScolaire,
  type NotionProgrammeDto,
  type PrerequisNotionDto,
  type StatutAttente,
} from '@silex/shared';

import type { BadgeTone } from './suivi-format';

/* ─────────────────────────── le chapitre ─────────────────────────── */

const MATIERES: Readonly<Record<Matiere, string>> = {
  francais: 'Français',
  mathematiques: 'Mathématiques',
};

/**
 * « Mathématiques · 3e secondaire » — où le chapitre s'enseigne.
 *
 * Un SOCLE de prérequis partagés (`CAP-01`, F-63) ne s'enseigne à aucun
 * niveau : son `niveau` n'est qu'un rattachement technique, et l'afficher
 * ferait lire « 1re secondaire » sur des notions d'avant le secondaire. Il se
 * dit donc par ce qu'il est : « Mathématiques · prérequis partagés ».
 */
export function situerChapitre(matiere: Matiere, niveau: NiveauScolaire, nature?: string): string {
  return nature !== undefined && chapitreEstUnSocle({ nature })
    ? `${MATIERES[matiere]} · ${LIBELLE_SOCLE}`
    : `${MATIERES[matiere]} · ${libelleNiveauScolaire(niveau)}`;
}

/** Ce que la console dit d'un socle, à la place d'un niveau. */
export const LIBELLE_SOCLE = 'prérequis partagés';

/** « chapitre 3 » — ou rien pour un socle, qui n'a pas de rang dans une classe. */
export function rangDuChapitre(c: {
  readonly rang: number;
  readonly nature: string;
}): string | null {
  return chapitreEstUnSocle(c) ? null : `chapitre ${c.rang}`;
}

const STATUTS: Readonly<Record<ContentStatus, { tone: BadgeTone; label: string }>> = {
  brouillon_IA: { tone: 'a-revoir', label: 'Brouillon' },
  en_revue: { tone: 'en-cours', label: 'En relecture' },
  valide: { tone: 'en-cours', label: 'Validé, non publié' },
  publie: { tone: 'acquis', label: 'Publié' },
  rejete: { tone: 'a-revoir', label: 'Rejeté' },
  retire: { tone: 'a-revoir', label: 'Retiré' },
};

/** Le statut d'un chapitre, en mots. Seul « Publié » est servi à un élève. */
export function statutChapitre(statut: ContentStatus): { tone: BadgeTone; label: string } {
  return STATUTS[statut];
}

/* ─────────────────────────── les notions ─────────────────────────── */

/** « 1.2 » — le rang d'une notion dans son chapitre, tel que l'élève le lit. */
export function rangNotion(rangChapitre: number, rang: number): string {
  return `${rangChapitre}.${rang}`;
}

/** Un prérequis, nommé : « 1.2 Multiplier… » s'il est du chapitre, son titre sinon. */
export function nommerPrerequis(p: PrerequisNotionDto, rangChapitre: number): string {
  return p.rangDansLeChapitre === null
    ? p.titre
    : `${rangNotion(rangChapitre, p.rangDansLeChapitre)} ${p.titre}`;
}

const FORMAT_SEANCES = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 1 });

/**
 * La part de rythme, dite en séances sur dix : `0,12` → « 1,2 ». `null` quand
 * le chapitre ne la donne pas — l'écran le dit, il n'affiche pas zéro.
 */
export function seancesSurDix(partRythme: number | null): string | null {
  if (partRythme === null || !Number.isFinite(partRythme)) return null;
  return FORMAT_SEANCES.format(partRythme * 10);
}

/* ─────────────────────────── les attentes ─────────────────────────── */

const STATUTS_ATTENTE: Readonly<Record<StatutAttente, string>> = {
  en_construction: 'En construction',
  maitrise_attendue: 'Maîtrise attendue',
  reinvestie: 'Réinvestie',
};

export function libelleStatutAttente(statut: StatutAttente): string {
  return STATUTS_ATTENTE[statut];
}

/**
 * Ce que le programme attend au niveau du chapitre : « Maîtrise attendue en
 * 3e secondaire ». Quand le programme n'en dit rien à ce niveau — ou dit des
 * choses différentes selon la séquence — la phrase le dit aussi.
 */
export function attenteAuNiveau(statut: StatutAttente | null, niveau: NiveauScolaire): string {
  return statut === null
    ? `Rien d’unique en ${libelleNiveauScolaire(niveau)}`
    : `${libelleStatutAttente(statut)} en ${libelleNiveauScolaire(niveau)}`;
}

/** « 4e secondaire (CST) : maîtrise attendue » — un jalon de la trajectoire. */
export function direJalon(j: JalonAttenteDto): string {
  const ou = `${libelleNiveauScolaire(j.niveau)}${j.sequence === null ? '' : ` (${j.sequence})`}`;
  return `${ou} : ${libelleStatutAttente(j.statut).toLocaleLowerCase('fr')}`;
}

/** Les quatre niveaux d'exercice, tels que MV-PRG-06 les nomme. */
const NIVEAUX_EXERCICE: Readonly<Record<number, string>> = {
  1: 'Facile',
  2: 'Intermédiaire',
  3: 'Avancé',
  4: 'Situation-problème',
};

/** « niveau 3 — Avancé », ou `null` si l'attente ne fixe pas de niveau. */
export function niveauExerciceAttendu(niveau: number | null): string | null {
  if (niveau === null) return null;
  const nom = NIVEAUX_EXERCICE[niveau];
  return nom === undefined ? null : `niveau ${niveau} — ${nom}`;
}

/** Une attente du chapitre, avec les notions qui la portent. */
export interface AttenteDuChapitre {
  readonly attente: AttenteNotionDto;
  /** Les notions qui la portent, dans l'ordre du chapitre. */
  readonly notions: readonly { readonly rang: number; readonly titre: string }[];
}

/**
 * Les attentes du chapitre, CHACUNE UNE FOIS, avec les notions qui la portent.
 * La réponse du serveur les range par notion ; un relecteur, lui, lit le
 * programme attente par attente — « qui porte l'exposant fractionnaire ? ».
 * L'ordre est celui où le chapitre les rencontre.
 */
export function attentesDuChapitre(notions: readonly NotionProgrammeDto[]): AttenteDuChapitre[] {
  const parCode = new Map<
    string,
    { attente: AttenteNotionDto; notions: { rang: number; titre: string }[] }
  >();
  for (const n of notions) {
    for (const a of n.attentes) {
      const entree = parCode.get(a.code) ?? { attente: a, notions: [] };
      entree.notions.push({ rang: n.rang, titre: n.titre });
      parCode.set(a.code, entree);
    }
  }
  return [...parCode.values()];
}

/* ─────────────────────────── les réemplois ─────────────────────────── */

export interface ReemploiDuChapitre {
  /** « Algèbre — multiplier et diviser des monômes et des polynômes ». */
  readonly ou: string;
  readonly niveau: string;
  /** `true` : le réemploi désigne un savoir qui existe en base. */
  readonly relie: boolean;
  readonly notions: readonly { readonly rang: number; readonly titre: string }[];
}

/**
 * Où le chapitre ressert, chaque destination une fois, avec les notions qui y
 * resservent. Deux réemplois sont le même s'ils disent le même lieu et le même
 * niveau.
 */
export function reemploisDuChapitre(notions: readonly NotionProgrammeDto[]): ReemploiDuChapitre[] {
  const parLieu = new Map<
    string,
    { ou: string; niveau: string; relie: boolean; notions: { rang: number; titre: string }[] }
  >();
  for (const n of notions) {
    for (const r of n.reemplois) {
      const cle = `${r.ou}|${r.niveau}`;
      const entree = parLieu.get(cle) ?? {
        ou: r.ou,
        niveau: r.niveau,
        relie: false,
        notions: [],
      };
      entree.relie = entree.relie || r.savoirCode !== undefined;
      entree.notions.push({ rang: n.rang, titre: n.titre });
      parLieu.set(cle, entree);
    }
  }
  return [...parLieu.values()];
}

/* ─────────────────────────── les résumés des sections ─────────────────────────── */

/** Le nombre d'arcs de prérequis du chapitre — entre ses notions, et vers l'avant. */
export function compterArcs(notions: readonly NotionProgrammeDto[]): number {
  return notions.reduce((n, notion) => n + notion.prerequis.length, 0);
}

/**
 * La ligne visible quand une section est REPLIÉE. C'est elle qui allège sans
 * cacher (MV-UX-01) : on sait ce qu'il y a derrière avant d'ouvrir.
 */
export function resumesDesSections(c: ChapitreProgrammeResponse): {
  readonly notions: string;
  readonly prerequis: string;
  readonly attentes: string;
  readonly reemplois: string;
  readonly retirees: string;
} {
  const attentes = attentesDuChapitre(c.notions);
  const enConstruction = attentes.filter((a) => a.attente.statutAuNiveau === 'en_construction');
  return {
    notions: denombrer(c.notions.length, 'notion'),
    prerequis: `${denombrer(c.appuis.length, 'acquis d’avant', 'acquis d’avant')} · ${denombrer(compterArcs(c.notions), 'lien')}`,
    attentes:
      attentes.length === 0
        ? 'Aucune attente rattachée'
        : `${denombrer(attentes.length, 'attente')}${enConstruction.length > 0 ? `, dont ${enConstruction.length} en construction` : ''}`,
    reemplois: denombrer(reemploisDuChapitre(c.notions).length, 'réemploi'),
    retirees: denombrer(c.retirees.length, 'notion remplacée', 'notions remplacées'),
  };
}

/** D'où vient un appui, pour la console : son origine en clair, et le savoir qui l'accueille. */
export function situerAppui(p: AppuiProgrammeDto): string {
  return `${p.origine} · rangé sous « ${p.savoir.enonce} »`;
}

/**
 * Ce qui existe pour REVOIR un appui (`CAP-01`) : « Contenu à revoir : une
 * fiche, 3 exercices ». Des comptes, et rien d'un niveau scolaire.
 */
export function direRessourcesDAppui(r: RessourcesDAppuiDto): string {
  const parts = [
    r.fiche === null ? null : 'une fiche',
    r.capsules > 0 ? denombrer(r.capsules, 'capsule') : null,
    r.exercices > 0 ? denombrer(r.exercices, 'exercice') : null,
  ].filter((x): x is string => x !== null);
  return `Contenu à revoir : ${parts.join(', ')}`;
}
